import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from "react-query";
import { useApi } from "@/hooks/useApi";
import { ASSISTANT_SESSIONS_QUERY_KEY } from "@/lib/assistant-sessions";
import type { AssistantSession } from "@/components/codesmith/types";

/**
 * Toast message for a failed autofix toggle. The backend rejects enabling
 * with a reason (e.g. "[code]smith is paused until the limit is raised or
 * a new billing month starts."); surface it instead of a generic failure.
 */
export function toggleAutofixErrorMessage(err: unknown, enabled: boolean, fallback = "Failed to toggle autofix."): string {
  const message = (err as {
    response?: {
      data?: {
        message?: string;
      };
    };
  } | null)?.response?.data?.message;
  if (!message) return fallback;
  return enabled ? `Autofix can't be enabled. ${message}` : message;
}

/**
 * Canonical React Query optimistic mutation for toggling autofix on a managed PR.
 *
 * Optimistically flips `autofix_enabled` in the session cache so the UI
 * responds instantly, rolls back on error, and re-syncs on settle.
 */
export function useToggleAutofix(sessionId) {
  const $ = _c(5);
  const api = useApi();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== api) {
    t0 = t1 => {
      const {
        prId,
        enabled
      } = t1;
      return api.patch(`/assistant/managed-prs/${prId}/autofix`, {
        enabled
      });
    };
    $[0] = api;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== queryClient || $[3] !== sessionId) {
    t1 = {
      onMutate: async t2 => {
        const {
          prId: prId_0,
          enabled: enabled_0
        } = t2;
        const sessionKey = ["assistantSession", sessionId];
        await queryClient.cancelQueries(sessionKey);
        const previousSession = queryClient.getQueryData(sessionKey);
        if (previousSession?.managed_prs) {
          queryClient.setQueryData(sessionKey, {
            ...previousSession,
            managed_prs: previousSession.managed_prs.map(pr => pr.id === prId_0 ? {
              ...pr,
              autofix_enabled: enabled_0
            } : pr)
          });
        }
        return {
          previousSession
        };
      },
      onError: (_err, _vars, context) => {
        if (context?.previousSession && sessionId) {
          queryClient.setQueryData(["assistantSession", sessionId], context.previousSession);
        }
      },
      onSettled: async () => {
        if (sessionId) {
          await queryClient.invalidateQueries(["assistantSession", sessionId]);
        }
        await queryClient.invalidateQueries(ASSISTANT_SESSIONS_QUERY_KEY);
      }
    };
    $[2] = queryClient;
    $[3] = sessionId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t0, t1);
}